import { PlanDraftRange, PlanDraftWork } from "./plan-draft";
import { PlanLinkEndResponse } from "./plan-dependencies";
import { ProjectPlanOrderResponse, ProjectPlanResponse, ProjectPlanRowResponse } from "./project-plan-builder";

/**
 * Согласование чужой части сдвига (этап 6.5 ТЗ «Графики»). Каскад по связям не толкает работы
 * проекта, на который у автора черновика нет прав: при применении такие толчки уходят ГИПу
 * того проекта запросом. ГИП видит предложенное контуром на графике и решает — согласовать
 * (сдвиг применяется от его имени) или отклонить (связь остаётся нарушенной, это конфликт).
 */

export interface PlanApprovalPush {
    kind: "work" | "section";
    orderId: string;
    assignmentId: string | null;
    contentGuid: string | null;
    employerId: string | null;
    /** «0530 · Безбородова М.А.» */
    label: string;
    shift: number;
    /** Из-за чего сдвиг: предшественник по связи, уже стоящий на новом месте */
    cause: PlanLinkEndResponse | null;
    /** «ФС +2» */
    linkCaption: string;
}

export interface PlanApproval {
    id: string;
    orderId: string;
    /** «0530 ЖД 20 лет Октября» */
    orderLabel: string;
    gipId: string | null;
    gipName: string;
    authorId: string;
    authorName: string;
    /** ISO */
    created: string;
    /** Пользователь может согласовать или отклонить */
    canDecide: boolean;
    pushes: PlanApprovalPush[];
    /** Работы, которые сдвинет согласование: прежнее и предложенное положение */
    works: PlanDraftWork[];
}

/**
 * Наложить предложенные сдвиги на ответ графика: у работ из запросов появляется контур на
 * предложенном месте. Контур — «призрак» (ghost): конфликты, связи и полосы проекта его не
 * считают, как и прежнее положение из черновика; отличается он только видом (proposed).
 * Ответ не мутируется.
 */
export function applyApprovals(response: ProjectPlanResponse, approvals: PlanApproval[]): ProjectPlanResponse {
    const works = approvals.flatMap((approval) => approval.works.map((work) => ({ approvalId: approval.id, work })));

    if (!works.length) {
        return response;
    }

    const touched = new Set(works.map((item) => item.work.orderId));

    return {
        ...response,
        orders: (response.orders || []).map((order) => (touched.has(order.id) ? applyToOrder(order, works) : order)),
    };
}

type ProposedWork = { approvalId: string; work: PlanDraftWork };

function applyToOrder(order: ProjectPlanOrderResponse, works: ProposedWork[]): ProjectPlanOrderResponse {
    const own = works.filter((item) => item.work.orderId === order.id);

    return {
        ...order,
        rows: (order.rows || []).map((row) => applyToRow(row, own, null)),
        sections: order.sections
            ? order.sections.map((section) => ({
                  ...section,
                  rows: (section.rows || []).map((row) => applyToRow(row, own, section.id || "")),
              }))
            : order.sections,
    };
}

/** section — раздел строки в режиме «Разделы», null — режим «Сотрудники» */
function applyToRow(
    row: ProjectPlanRowResponse,
    works: ProposedWork[],
    section: string | null
): ProjectPlanRowResponse {
    if (row.kind !== "employer") {
        return row;
    }

    const mine = works.filter(
        (item) => item.work.employerId === row.id && (section === null || (item.work.contentGuid || "") === section)
    );

    if (!mine.length) {
        return row;
    }

    const proposed = mine.flatMap((item) =>
        item.work.after.map((range) => toSegment(range, item.work.id, item.approvalId))
    );

    return { ...row, segments: [...(row.segments || []), ...proposed] };
}

function toSegment(range: PlanDraftRange, assignmentId: string, approvalId: string) {
    return {
        from: range.from,
        to: range.to,
        stopDay: range.to,
        workDays: range.workDays,
        continuesLeft: false,
        continuesRight: false,
        assignmentId,
        ghost: true,
        proposed: approvalId,
    };
}

/** «+3 раб. дн.» */
export const shiftCaption = (shift: number) => `+${shift} раб. дн.`;
